import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LockIcon from "@mui/icons-material/Lock";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import { useTranslation } from "react-i18next";

const STATE = {
  earned: {
    borderColor: "#FFD700",
    shadow: "0 4px 24px rgba(255,215,0,0.35)",
    cardBorder: "2px solid #FFD700",
    chipBg: "#fffde7",
    chipColor: "#b8860b",
    chipIcon: <EmojiEventsIcon style={{ fontSize: 13, color: "#b8860b" }} />,
  },
  inProgress: {
    borderColor: "#1565c0",
    shadow: "0 4px 20px rgba(21,101,192,0.18)",
    cardBorder: "2px solid #bbdefb",
    chipBg: "#e3f2fd",
    chipColor: "#1565c0",
    chipIcon: <TrackChangesIcon style={{ fontSize: 13, color: "#1565c0" }} />,
  },
  locked: {
    borderColor: "#bdbdbd",
    shadow: "none",
    cardBorder: "2px solid #eeeeee",
    chipBg: "#f5f5f5",
    chipColor: "#757575",
    chipIcon: <LockIcon style={{ fontSize: 13, color: "#757575" }} />,
  },
};

function AchievementCard({ logo, name, description, progress }) {
  const { t } = useTranslation();

  const isEarned = progress === 100;
  const isInProgress = progress > 0 && progress < 100;
  const state = isEarned ? STATE.earned : isInProgress ? STATE.inProgress : STATE.locked;

  const chipLabel = isEarned
    ? t("achievements.earned")
    : isInProgress
    ? t("achievements.inProgress")
    : t("achievements.locked");

  return (
    <Card
      sx={{
        borderRadius: 3,
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        border: state.cardBorder,
        boxShadow: state.shadow,
        bgcolor: isInProgress ? "rgba(227,242,253,0.18)" : "background.paper",
        transition: "box-shadow 0.2s",
      }}
    >
      <MDBox
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          overflow: "hidden",
          border: `4px solid ${state.borderColor}`,
          boxShadow: isEarned ? `0 0 18px rgba(255,215,0,0.5)` : "none",
          filter: !isEarned && !isInProgress ? "grayscale(75%) opacity(0.55)" : "none",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </MDBox>

      <Chip
        icon={state.chipIcon}
        label={chipLabel}
        size="small"
        sx={{
          bgcolor: state.chipBg,
          color: state.chipColor,
          fontWeight: "bold",
          fontSize: "0.7rem",
          height: 22,
          borderRadius: 1,
          mb: 1.5,
          "& .MuiChip-icon": { color: state.chipColor },
        }}
      />

      <MDTypography variant="h6" fontWeight="bold" lineHeight={1.3} mb={0.5}>
        {name}
      </MDTypography>

      <MDTypography
        variant="body2"
        color="secondary"
        lineHeight={1.5}
        sx={{ flex: 1, mb: 2 }}
      >
        {description}
      </MDTypography>

      <MDBox width="100%">
        {!isEarned && !isInProgress ? (
          <MDBox sx={{ height: 6, bgcolor: "#eeeeee", borderRadius: 2 }} />
        ) : (
          <>
            <MDProgress value={Math.round(progress)} color={isEarned ? "success" : "info"} />
            <MDTypography
              variant="caption"
              color="secondary"
              sx={{ display: "block", textAlign: "center", mt: 0.5 }}
            >
              {Math.round(progress)}%
            </MDTypography>
          </>
        )}
      </MDBox>
    </Card>
  );
}

AchievementCard.propTypes = {
  logo: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  progress: PropTypes.number,
};

export default AchievementCard;
